import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined"; // Importing the Lock icon
import Typography from "@mui/material/Typography";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardFooter from "components/Card/CardFooter.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import SimpleAlert from "../../components/AtutaAlerts/CustomAlerts";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useAuth } from "../../context/AuthContext";
import AuthApi from "../../api_calls/AuthApi";

import loginPageStyles from "assets/jss/material-dashboard-pro-react/views/loginPageStyle.js";

const useStyles = makeStyles((theme) => ({
  ...loginPageStyles,
  errorText: {
    color: theme.palette.error.main || "red",
    fontSize: "12px",
    marginTop: "5px",
    textAlign: "left",
    paddingLeft: "15px",
  },
  // New style for the title container
  titleContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: "20px", // Space between title and card
    marginLeft: "20px", // Align with the card's intended left padding
    // You might want to get this padding from a theme variable if available
  },
  titleIcon: {
    marginRight: "10px", // Space between icon and text
    color: theme.palette.primary.main || "#9c27b0", // Example primary color
    fontSize: "2rem", // Adjust size as needed
  },
  pageTitle: {
    fontSize: "1.8rem", // Make the title slightly larger
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
}));

export default function ChangePassword() {
  const classes = useStyles();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const { user } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlertMessage("");
    setErrors({});

    let formValid = true;

    if (!user?.email) {
      setAlertMessage(
        "User email is not available. Please try again or log in."
      );
      formValid = false;
    }

    if (!currentPassword) {
      setErrors((prev) => ({
        ...prev,
        currentPassword: "Current password is required.",
      }));
      formValid = false;
    }
    if (!newPassword) {
      setErrors((prev) => ({
        ...prev,
        newPassword: "New password is required.",
      }));
      formValid = false;
    } else if (newPassword.length < 6) {
      setErrors((prev) => ({
        ...prev,
        newPassword: "Password must be at least 6 characters.",
      }));
      formValid = false;
    }
    if (newPassword !== confirmNewPassword) {
      setErrors((prev) => ({
        ...prev,
        confirmNewPassword: "Passwords do not match.",
      }));
      formValid = false;
    }

    if (!formValid) {
      return;
    }

    setLoading(true);
    try {
      console.log(
        "Attempting to change password for user with email:",
        user?.email
      );
      console.log("Current Password (sent to API):", currentPassword);
      console.log("New Password (sent to API):", newPassword);

      const response = await AuthApi.changePassword({
        email: user?.email,
        oldPassword: currentPassword,
        newPassword: newPassword,
      });

      console.log("API Change Password Response:", response);

      if (response.status === "success") {
        setAlertMessage("Password changed successfully!");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmNewPassword("");
      } else {
        setAlertMessage(
          response.data?.message ||
            response.message ||
            "Failed to change password. Please try again."
        );
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "An unexpected error occurred during password change. Please try again.";
      console.error("Error changing password:", error);
      setAlertMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <GridContainer justify="left">
        <GridItem xs={12} sm={6} md={6}>
          {/* Title moved outside the Card */}
          <Typography
            sx={{
              fontWeight: "normal",
              fontSize: "15px",
              fontFamily: "Calibri, sans-serif",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <LockOutlinedIcon /> Change Password
          </Typography>

          <form onSubmit={handleSubmit}>
            <Card login>
              {/* CardHeader and old h4 removed */}
              <CardBody style={{ textAlign: "center" }}>
                <p style={{ fontSize: "14px", marginBottom: "20px" }}>
                  Please enter your current password and your new password.
                </p>
                <CustomInput
                  labelText="Current Password..."
                  id="current-password"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <span
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            setShowCurrentPassword(!showCurrentPassword)
                          }
                        >
                          {showCurrentPassword ? (
                            <Visibility />
                          ) : (
                            <VisibilityOff />
                          )}
                        </span>
                      </InputAdornment>
                    ),
                    type: showCurrentPassword ? "text" : "password",
                    autoComplete: "current-password",
                    value: currentPassword,
                    onChange: (e) => setCurrentPassword(e.target.value),
                    error: !!errors.currentPassword,
                  }}
                />
                {errors.currentPassword && (
                  <p className={classes.errorText}>{errors.currentPassword}</p>
                )}

                <CustomInput
                  labelText="New Password..."
                  id="new-password"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <span
                          style={{ cursor: "pointer" }}
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? <Visibility /> : <VisibilityOff />}
                        </span>
                      </InputAdornment>
                    ),
                    type: showNewPassword ? "text" : "password",
                    autoComplete: "new-password",
                    value: newPassword,
                    onChange: (e) => setNewPassword(e.target.value),
                    error: !!errors.newPassword,
                  }}
                />
                {errors.newPassword && (
                  <p className={classes.errorText}>{errors.newPassword}</p>
                )}

                <CustomInput
                  labelText="Confirm New Password..."
                  id="confirm-new-password"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <span
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            setShowConfirmNewPassword(!showConfirmNewPassword)
                          }
                        >
                          {showConfirmNewPassword ? (
                            <Visibility />
                          ) : (
                            <VisibilityOff />
                          )}
                        </span>
                      </InputAdornment>
                    ),
                    type: showConfirmNewPassword ? "text" : "password",
                    autoComplete: "new-password",
                    value: confirmNewPassword,
                    onChange: (e) => setConfirmNewPassword(e.target.value),
                    error: !!errors.confirmNewPassword,
                  }}
                />
                {errors.confirmNewPassword && (
                  <p className={classes.errorText}>
                    {errors.confirmNewPassword}
                  </p>
                )}
              </CardBody>
              <CardFooter className={classes.justifyContentCenter}>
                <Button color="rose" type="submit" disabled={loading}>
                  {loading ? (
                    <CircularProgress size={24} style={{ color: "#fff" }} />
                  ) : (
                    "Change Password"
                  )}
                </Button>
              </CardFooter>
            </Card>
            {alertMessage && (
              <SimpleAlert
                message={alertMessage}
                onClose={() => setAlertMessage("")}
              />
            )}
          </form>
        </GridItem>
      </GridContainer>
    </div>
  );
}
